// -----------------------------------------------------------------------------
// src/pages/conversations/CreateNewChatDialog.js – UPDATED COMPONENT
// -----------------------------------------------------------------------------
// Allows creating a new conversation by setting its name, cover image, and theme.

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Button,
} from '@mui/material';
import { useMutation } from 'react-query';
import SelectAssetDialog from '../tools/selectfile';
import API from '../../API';

/**
 * Props:
 *  - open (bool): dialog visible
 *  - onClose (fn): callback when user cancels or after creation
 *  - onCreated (fn): callback(conversation_id, fullResponse) on success
 */
export default function CreateNewChatDialog({ open, onClose, onCreated }) {
  const [displayName, setDisplayName] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [themeColor, setThemeColor] = useState('#ffffff');
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);

  const createMutation = useMutation(
    payload => API.conversations.createConversation(payload),
    {
      onSuccess: res => {
        if (onCreated) onCreated(res.conversation_id, res);
        handleClose();
      },
      onError: err => {
        console.error('Error creating conversation', err);
        alert(err?.response?.data?.error || err.message || 'Something went wrong');
      },
    }
  );

  // Build payload with only metadata
  const buildPayload = () => ({
    display_name: displayName.trim(),
    cover_image: coverImage,
    theme: { color: themeColor },
  });

  const handleSave = () => {
    if (!displayName.trim()) return;
    createMutation.mutate(buildPayload());
  };

  const handleClose = () => {
    setTimeout(() => {
      setDisplayName('');
      setCoverImage('');
      setThemeColor('#ffffff');
    }, 250);
    onClose();
  };

  const handleAssetPick = file => {
    setCoverImage(file.filename);
    setAssetDialogOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>New Conversation Details</DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Conversation Name */}
          <TextField
            label="Conversation Name"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            fullWidth
            required
            disabled={createMutation.isLoading}
          />

          {/* Cover Image */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Cover Image
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                fullWidth
                placeholder="Image filename or URL"
                value={coverImage}
                onChange={e => setCoverImage(e.target.value)}
                disabled={createMutation.isLoading}
              />
              <Button
                variant="outlined"
                onClick={() => setAssetDialogOpen(true)}
                disabled={createMutation.isLoading}
              >
                Select
              </Button>
            </Box>
          </Box>

          {/* Theme Color */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Theme Color
            </Typography>
            <input
              type="color"
              value={themeColor}
              onChange={e => setThemeColor(e.target.value)}
              style={{ width: '100%', height: 44, border: 'none', padding: 0 }}
              disabled={createMutation.isLoading}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={createMutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isLoading || !displayName.trim()}
          >
            {createMutation.isLoading ? 'Creating…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Asset Picker */}
      <SelectAssetDialog
        open={assetDialogOpen}
        onClose={() => setAssetDialogOpen(false)}
        selectedAsset={null}
        onAssetClick={handleAssetPick}
      />
    </>
  );
}
